import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, Typography, makeStyles } from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import type { FindingCategory, FindingResolutionState, SpecIntelligenceFinding } from 'api/graphQL/types/specIntelligence.types'
import { ALL_FINDING_CATEGORIES, CATEGORY_COLORS, CATEGORY_DISPLAY_LABELS, STATE_COLORS, STATE_ICONS } from 'components/SpecIntelligence/specIntelligenceConstants'
import React, { useMemo } from 'react'

/** Category filter value: a specific category, or 'All' to show every category. */
export type CategoryFilterValue = FindingCategory | 'All'

/** State filter value: a specific resolution state, or 'All' to show every state. */
export type StateFilterValue = FindingResolutionState | 'All'

const CATEGORY_OPTIONS: CategoryFilterValue[] = ['All', ...ALL_FINDING_CATEGORIES]
const STATE_OPTIONS: StateFilterValue[] = ['All', 'Open', 'Resolved', 'Dismissed']

const useStyles = makeStyles((theme: Theme) => ({
    filterRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(2.5),
        padding: theme.spacing(1.25, 2.5),
        backgroundColor: '#FCFDFF',
    },
    filterGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    filterLabel: {
        fontSize: pxToRem(11),
        fontWeight: 600,
        color: '#6B7280',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
    },
    toggleGroup: {
        gap: pxToRem(4),
    },
    toggleButton: {
        border: '1px solid transparent',
        borderRadius: `${pxToRem(20)} !important`,
        padding: `${pxToRem(8)} ${pxToRem(10)}`,
        fontSize: pxToRem(12),
        fontWeight: 400,
        textTransform: 'none',
        color: '#424242',
        '&.Mui-selected': {
            backgroundColor: '#EAEFF5',
            color: '#043852',
            border: `1px solid #043852`,
            fontWeight: 600,
            '&:hover': { backgroundColor: '#EAEFF5' },
        },
    },
    optionContent: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: pxToRem(4),
        lineHeight: 1,
    },
    categoryDot: {
        width: pxToRem(14),
        height: pxToRem(14),
        borderRadius: '50%',
        flexShrink: 0,
        display: 'block',
        boxSizing: 'border-box',
    },
    stateIcon: {
        fontSize: pxToRem(14),
        display: 'block',
    },
    optionLabel: {
        display: 'inline-block',
        lineHeight: pxToRem(14),
    },
    optionCount: {
        opacity: 0.7,
    },
}))

/** Props for {@link InsightsRunFilterBar}. */
export interface InsightsRunFilterBarProps {
    /** The selected run's findings, used to compute the per-category counts shown on each pill. */
    findings: SpecIntelligenceFinding[]
    /** The current Category filter value. */
    categoryFilter: CategoryFilterValue
    /** The current State filter value. */
    stateFilter: StateFilterValue
    /** Called with the new Category filter value when the user changes it. */
    onCategoryChange: (value: CategoryFilterValue) => void
    /** Called with the new State filter value when the user changes it. */
    onStateChange: (value: StateFilterValue) => void
}

/** Labeled Category and State single-select toggle-button groups for the Insights run detail view. */
const InsightsRunFilterBar = ({ findings, categoryFilter, stateFilter, onCategoryChange, onStateChange }: InsightsRunFilterBarProps) => {
    const classes = useStyles()

    const { categoryCounts, stateCounts } = useMemo(() => {
        const categoryCounts: Record<CategoryFilterValue, number> = {
            All: findings.length,
            CrossReferences: 0,
            ErrorsAndOmissions: 0,
            Contradictions: 0,
            Coordination: 0,
        }
        const stateCounts: Record<StateFilterValue, number> = {
            All: findings.length,
            Open: 0,
            Resolved: 0,
            Dismissed: 0,
        }
        findings.forEach((finding) => {
            categoryCounts[finding.category] += 1
            stateCounts[finding.resolutionState] += 1
        })
        return { categoryCounts, stateCounts }
    }, [findings])

    return (
        <div className={classes.filterRow}>
            <div className={classes.filterGroup}>
                <Typography component="span" id="insights-category-filter-label" className={classes.filterLabel}>
                    Category
                </Typography>
                <ToggleButtonGroup
                    value={categoryFilter}
                    exclusive
                    onChange={(_, value) => value !== null && onCategoryChange(value as CategoryFilterValue)}
                    aria-labelledby="insights-category-filter-label"
                    role="radiogroup"
                    className={classes.toggleGroup}
                >
                    {CATEGORY_OPTIONS.map((option) => (
                        <ToggleButton key={option} value={option} className={classes.toggleButton}>
                            <span className={classes.optionContent}>
                                {option !== 'All' && (
                                    <span className={classes.categoryDot} style={{ backgroundColor: CATEGORY_COLORS[option].backgroundColor }} />
                                )}
                                {option === 'All' ? 'All' : CATEGORY_DISPLAY_LABELS[option]}
                                <span className={classes.optionCount}>({categoryCounts[option]})</span>
                            </span>
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </div>
            <div className={classes.filterGroup}>
                <Typography component="span" id="insights-state-filter-label" className={classes.filterLabel}>
                    State
                </Typography>
                <ToggleButtonGroup
                    value={stateFilter}
                    exclusive
                    onChange={(_, value) => value !== null && onStateChange(value as StateFilterValue)}
                    aria-labelledby="insights-state-filter-label"
                    role="radiogroup"
                    className={classes.toggleGroup}
                >
                    {STATE_OPTIONS.map((option) => {
                        const StateIcon = option !== 'All' ? STATE_ICONS[option] : null
                        const stateColor = option !== 'All' ? STATE_COLORS[option].color : undefined
                        return (
                            <ToggleButton key={option} value={option} className={classes.toggleButton}>
                                <span className={classes.optionContent}>
                                    {StateIcon && <StateIcon className={classes.stateIcon} style={{ color: stateColor }} />}
                                    <span className={classes.optionLabel}>{option}</span>
                                    <span className={classes.optionCount}>({stateCounts[option]})</span>
                                </span>
                            </ToggleButton>
                        )
                    })}
                </ToggleButtonGroup>
            </div>
        </div>
    )
}

export default InsightsRunFilterBar
